import { faHeart } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DialogContent, Stack, Typography } from "@mui/material"
import { useCallback, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { StandardButton } from "../../../../buttons/StandardButton"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { FilterSearch } from "../../FilterSearch"

import type { MeetingServicePriceListsItem } from "./priceListsTypes"
import { SelectPriceListsCard } from "./SelectPriceListsCard"
import { savePriceListFavorite } from "./priceListsApi"
import { filterPriceList } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "2.5rem",
        paddingTop: "0",
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        color: theme.palette.tailwind.slate[950],
        minHeight: "28rem",
    },
    iconDark: {
        color: theme.palette.tailwind.slate[950],
    },
    emptyWrap: {
        textAlign: "center",
        padding: "2rem 1rem",
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        flex: 1,
        justifyContent: "center",
    },
}))
export type SelectPriceListsFiltersTypes = Readonly<{
    showFavoritesOnly: boolean
    search: string
}>

// const transformData = (data: PriceListFavoritesResponse) => {
//     return data.merchants
// }

type SelectPriceListsDialogProps = Readonly<{
    userId: number
    onClose: () => void
    merchants: MeetingServicePriceListsItem[]
    onSelect: (item: SelectedPriceListItem) => void
}>

export function SelectPriceListsDialog({ onClose, merchants, userId, onSelect }: SelectPriceListsDialogProps) {
    const { classes } = useStyles()
    const [filters, setFilters] = useState<SelectPriceListsFiltersTypes>({ showFavoritesOnly: false, search: "" })

    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )
    // const { data: favoritesList = [] } = useApiQuery(fetchPriceListFavorites, {
    //     queryName: MeetingServicePriceListsQuery.USER_MEETING_SERVICE_PRICE_LISTS_FAVORITES,
    //     dependencies: { userId },
    //     select: transformData,
    // })

    const filteredMerchants = useMemo(
        () => filterPriceList(filters.showFavoritesOnly, searchRegex, merchants),
        [merchants, searchRegex, filters.showFavoritesOnly],
    )

    const favoriteItemsCount = useMemo(
        () =>
            merchants.reduce((count, merchant) => count + merchant.priceLists.filter((pl) => pl.isFavorite).length, 0),
        [merchants],
    )
    const totalPriceListsCount = useMemo(
        () => merchants.reduce((count, merchant) => count + merchant.priceLists.length, 0),
        [merchants],
    )

    const { mutate: callSavePriceListFavorite } = useApiMutation(savePriceListFavorite, {
        async onSuccess(_, _data) {},
    })

    const handleFavoriteClick = (merchantId: number, priceListId: number) => {
        callSavePriceListFavorite({
            userId,
            merchants: [
                {
                    id: merchantId,
                    priceList: {
                        id: priceListId,
                    },
                },
            ],
        })
    }
    const handleSearch = useCallback((searchText: string) => {
        setFilters((prev) => ({ ...prev, search: searchText }))
    }, [])

    const toggleFavoritesFilter = useCallback(() => {
        setFilters((prev) => ({ ...prev, showFavoritesOnly: !prev.showFavoritesOnly }))
    }, [])

    const resetAllFilters = useCallback(() => {
        setFilters({ showFavoritesOnly: false, search: "" })
    }, [])

    const hasAnyPriceLists = totalPriceListsCount > 0

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="create-order-select-price-list-dialog">
                    <>Select price list</>
                </LocalizedStrict>
            }
            open
            onClose={onClose}
            maxWidthProp="lg"
            scroll="paper"
        >
            <DialogContent className={classes.dialogContent}>
                <Stack gap=".5rem" flexDirection="row" justifyContent="flex-end" flexWrap="wrap" alignItems="center">
                    <StandardButton
                        variant={filters.showFavoritesOnly ? "contained" : "text"}
                        size="small"
                        fullWidth={false}
                        startIcon={<FontAwesomeIcon icon={faHeart} />}
                        onClick={toggleFavoritesFilter}
                        disabled={favoriteItemsCount === 0}
                    >
                        <LocalizedStrict
                            id="create-order-select-price-list-favorites-section"
                            vars={{ count: favoriteItemsCount }}
                        >
                            <Typography variant="subtitle1">My favorites ({favoriteItemsCount})</Typography>
                        </LocalizedStrict>
                    </StandardButton>
                    {!!totalPriceListsCount && (
                        <StandardButton variant="text" size="small" fullWidth={false} onClick={resetAllFilters}>
                            <LocalizedStrict
                                id="create-order-select-price-list-favorites-view-all"
                                vars={{ count: totalPriceListsCount }}
                            >
                                <Typography variant="subtitle1">View all ({totalPriceListsCount})</Typography>
                            </LocalizedStrict>
                        </StandardButton>
                    )}
                    <FilterSearch search={filters.search} setSearch={handleSearch} activeWidth="20rem" />
                </Stack>

                {filteredMerchants.length > 0 ? (
                    filteredMerchants.map((merchant) => (
                        <SelectPriceListsCard
                            merchant={merchant}
                            onSelect={onSelect}
                            searchRegex={searchRegex}
                            onClose={onClose}
                            handleFavoriteClick={handleFavoriteClick}
                            key={merchant.id}
                        />
                    ))
                ) : !hasAnyPriceLists ? (
                    <Stack className={classes.emptyWrap}>
                        <LocalizedStrict id="create-order-select-price-list-no-price-lists-title">
                            <Typography variant="h6">
                                Currently, there are no price lists available. Please reach out to the merchant to
                                request access to them.
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                ) : (
                    <Stack className={classes.emptyWrap}>
                        <LocalizedStrict id="create-order-select-price-list-no-search-results">
                            <Typography variant="h6">Your search returned no results</Typography>
                        </LocalizedStrict>
                    </Stack>
                )}
            </DialogContent>
        </StandardDialog>
    )
}
